/**
 * Taxonomies: the value lists the rest of the console picks from — request
 * type, priority, return reason, AO status, VC status.
 *
 * Values are edited in place: renaming updates labels everywhere, and the API
 * refuses to delete a value that labels recorded runs (reporting groups by
 * these strings). The classification prompt is editable too, because that is
 * what actually decides how conversations get stamped.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from '../api/client';
import { useBrands } from '../api/admin';
import { Badge, Button, Card, CardHeader } from '../components/ui/primitives';

interface TaxonomyValueRow {
  id: string;
  value: string;
  definition: string | null;
  position: number;
}

interface TaxonomyRow {
  id: string;
  key: string;
  title: string;
  prompt: string;
  enabled: boolean;
  required: boolean;
  channels: string[];
  scope: 'shared' | 'brand';
  values: TaxonomyValueRow[];
}

export function TaxonomiesPage() {
  const brands = useBrands();
  const [brandId, setBrandId] = useState('');

  const scopeQuery = brandId ? `?brandId=${brandId}` : '';
  const list = useQuery({
    queryKey: ['taxonomies', brandId || 'shared'],
    queryFn: () => apiGet<TaxonomyRow[]>(`/api/taxonomies${scopeQuery}`),
  });

  if (list.isLoading) {
    return <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>;
  }

  return (
    <div className="mx-auto max-w-[1100px] px-5 py-5">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="tax-brand" className="eyebrow mb-1 block text-ink-500">
            In force for
          </label>
          <select
            id="tax-brand"
            value={brandId}
            onChange={(event) => setBrandId(event.target.value)}
            className="rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink-900"
          >
            <option value="">Shared (every brand inherits)</option>
            {(brands.data ?? []).map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>
        </div>
        <p className="text-2xs text-ink-400">
          Renaming a value updates labels everywhere. Deleting one that labels recorded runs is
          refused — rename it instead.
        </p>
      </div>

      {(list.data ?? []).length === 0 ? (
        <Card>
          <p className="px-4 py-10 text-center text-sm text-ink-400">
            No taxonomies in this scope.
          </p>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {(list.data ?? []).map((taxonomy) => (
            <TaxonomyCard key={taxonomy.id} taxonomy={taxonomy} />
          ))}
        </div>
      )}
    </div>
  );
}

function TaxonomyCard({ taxonomy }: { taxonomy: TaxonomyRow }) {
  const queryClient = useQueryClient();
  const [showPrompt, setShowPrompt] = useState(false);
  const [prompt, setPrompt] = useState(taxonomy.prompt);
  const [newValue, setNewValue] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['taxonomies'] });
  const fail = (error: unknown) =>
    setProblem(error instanceof ApiError ? error.message : 'That did not go through.');

  const update = useMutation({
    mutationFn: (body: { prompt?: string; enabled?: boolean; required?: boolean }) =>
      apiPatch(`/api/taxonomies/${taxonomy.id}`, body),
    onSuccess: async () => {
      setProblem(null);
      await refresh();
    },
    onError: fail,
  });

  const addValue = useMutation({
    mutationFn: () => apiPost(`/api/taxonomies/${taxonomy.id}/values`, { value: newValue.trim() }),
    onSuccess: async () => {
      setNewValue('');
      setProblem(null);
      await refresh();
    },
    onError: fail,
  });

  const removeValue = useMutation({
    mutationFn: (valueId: string) => apiDelete(`/api/taxonomies/values/${valueId}`),
    onSuccess: async () => {
      setProblem(null);
      await refresh();
    },
    onError: fail,
  });

  const renameValue = useMutation({
    mutationFn: (input: { valueId: string; value: string }) =>
      apiPatch(`/api/taxonomies/values/${input.valueId}`, { value: input.value }),
    onSuccess: async () => {
      setProblem(null);
      await refresh();
    },
    onError: fail,
  });

  return (
    <Card>
      <CardHeader
        title={taxonomy.title}
        meta={
          <span className="font-mono">
            {taxonomy.key}
            {taxonomy.required ? ' · required' : ''}
          </span>
        }
        action={
          <div className="flex items-center gap-2">
            <Badge tone={taxonomy.scope === 'brand' ? 'accent' : 'neutral'}>{taxonomy.scope}</Badge>
            <Button
              size="sm"
              variant={taxonomy.enabled ? 'secondary' : 'ghost'}
              onClick={() => update.mutate({ enabled: !taxonomy.enabled })}
              disabled={update.isPending}
            >
              {taxonomy.enabled ? 'Enabled' : 'Disabled'}
            </Button>
          </div>
        }
      />

      <div className="px-4 py-3">
        <ul className="space-y-1.5">
          {taxonomy.values.map((value) => (
            <ValueRow
              key={value.id}
              value={value}
              onRename={(text) => renameValue.mutate({ valueId: value.id, value: text })}
              onDelete={() => removeValue.mutate(value.id)}
            />
          ))}
          {taxonomy.values.length === 0 ? (
            <li className="text-xs text-ink-400">No values yet.</li>
          ) : null}
        </ul>

        <div className="mt-2.5 flex gap-1.5">
          <input
            value={newValue}
            onChange={(event) => setNewValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && newValue.trim()) addValue.mutate();
            }}
            placeholder="Add a value…"
            aria-label={`Add a value to ${taxonomy.title}`}
            className="min-w-0 flex-1 rounded border border-line bg-surface px-2 py-1 text-xs text-ink-900"
          />
          <Button
            size="sm"
            disabled={!newValue.trim() || addValue.isPending}
            onClick={() => addValue.mutate()}
          >
            <Plus size={13} strokeWidth={2} aria-hidden />
            Add
          </Button>
        </div>

        <button
          type="button"
          onClick={() => setShowPrompt((open) => !open)}
          aria-expanded={showPrompt}
          className="mt-3 text-2xs text-ink-400 underline hover:text-ink-900"
        >
          {showPrompt ? 'Hide the classification prompt' : 'Edit the classification prompt'}
        </button>

        {showPrompt ? (
          <div className="mt-2">
            <textarea
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              rows={5}
              aria-label={`Classification prompt for ${taxonomy.title}`}
              className="w-full rounded border border-line bg-surface px-2.5 py-2 text-xs text-ink-700"
            />
            <Button
              size="sm"
              className="mt-1.5"
              disabled={update.isPending || prompt.trim() === taxonomy.prompt}
              onClick={() => update.mutate({ prompt: prompt.trim() })}
            >
              Save prompt
            </Button>
          </div>
        ) : null}

        {problem ? <p className="mt-2 text-xs text-stop-700">{problem}</p> : null}
      </div>
    </Card>
  );
}

function ValueRow({
  value,
  onRename,
  onDelete,
}: {
  value: TaxonomyValueRow;
  onRename: (text: string) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value.value);

  return (
    <li className="flex items-center gap-2">
      {editing ? (
        <input
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && text.trim()) {
              onRename(text.trim());
              setEditing(false);
            }
            if (event.key === 'Escape') {
              setText(value.value);
              setEditing(false);
            }
          }}
          onBlur={() => {
            if (text.trim() && text.trim() !== value.value) onRename(text.trim());
            setEditing(false);
          }}
          autoFocus
          aria-label={`Rename ${value.value}`}
          className="min-w-0 flex-1 rounded border border-line bg-surface px-2 py-0.5 text-xs text-ink-900"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="min-w-0 flex-1 truncate text-left text-xs text-ink-900 hover:text-accent-700"
          title={value.definition ?? undefined}
        >
          {value.value}
          {value.definition ? (
            <span className="ml-1.5 text-2xs text-ink-400">— {value.definition}</span>
          ) : null}
        </button>
      )}
      <Button size="sm" variant="ghost" aria-label={`Delete ${value.value}`} onClick={onDelete}>
        <Trash2 size={12} strokeWidth={2} aria-hidden />
      </Button>
    </li>
  );
}
